import { useAppState } from '@/src/common/hooks/useAppState'
import * as Updates from 'expo-updates'
import { type PropsWithChildren, useEffect, useState } from 'react'
import { Platform, View, Text } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Logo } from '@/src/assets/icons/Logo'
import { Spinner } from 'tamagui'
import { useIsFirstRun } from '@/src/common/hooks/useIsFirstRun'
import { DateTime } from 'luxon'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { mesaColorNames, typography } from '@/src/common/theme'
// import { useLogError } from '@/src/common/hooks/useLogError'

const FIRST_RUN_UPDATE_DELAY = 3000

async function storageGetWithFalsyOnFail(...args: Parameters<typeof AsyncStorage.getItem>) {
  try {
    return await AsyncStorage.getItem(...args)
  } catch {
    return undefined
  }
}

async function storageSetWithNoopOnFail(...args: Parameters<typeof AsyncStorage.setItem>) {
  try {
    await AsyncStorage.setItem(...args)
  } catch {
    return
  }
}

async function retrieveOTAMeta() {
  const CHECKED_FOR_OTA_UPDATE_AT = 'checked_for_ota_update_at'
  const APPLIED_OTA_UPDATE_AT = 'applied_ota_update_at'
  const now = DateTime.now()

  const lastCheckedAt = await storageGetWithFalsyOnFail(CHECKED_FOR_OTA_UPDATE_AT)
  const lastOTAUpdateAppliedAt = await storageGetWithFalsyOnFail(APPLIED_OTA_UPDATE_AT)

  return {
    now,
    isMoreThan1MinuteSinceLastCheck: lastCheckedAt
      ? DateTime.fromISO(lastCheckedAt).plus({ minutes: 1 }) < now
      : true,
    isMoreThan1MinuteSinceLastOTAUpdateApplied: lastOTAUpdateAppliedAt
      ? DateTime.fromISO(lastOTAUpdateAppliedAt).plus({ minutes: 1 }) < now
      : true,
    APPLIED_OTA_UPDATE_AT,
    CHECKED_FOR_OTA_UPDATE_AT,
  } as const
}

function MobileOTA({ children }: PropsWithChildren) {
  const { state } = useAppState()
  const { isUpdateAvailable, isUpdatePending, isDownloading, downloadError, checkError } =
    Updates.useUpdates()
  const appStateIsResumed = 'active' === state
  // const logError = useLogError()

  useEffect(() => {
    const checkForUpdate = async () => {
      const { now, isMoreThan1MinuteSinceLastCheck, CHECKED_FOR_OTA_UPDATE_AT } =
        await retrieveOTAMeta()

      if (isMoreThan1MinuteSinceLastCheck) {
        await storageSetWithNoopOnFail(CHECKED_FOR_OTA_UPDATE_AT, now.toISO())
        await Updates.checkForUpdateAsync()
      }
    }

    void checkForUpdate()
  }, [appStateIsResumed])

  useEffect(() => {
    const fetchUpdate = async () => {
      const { isMoreThan1MinuteSinceLastOTAUpdateApplied } = await retrieveOTAMeta()

      if (isMoreThan1MinuteSinceLastOTAUpdateApplied) {
        await Updates.fetchUpdateAsync()
      }
    }

    if (isUpdateAvailable && !isDownloading) {
      void fetchUpdate()
    }
  }, [isUpdateAvailable, isDownloading])

  useEffect(() => {
    const applyUpdate = async () => {
      const { isMoreThan1MinuteSinceLastOTAUpdateApplied, APPLIED_OTA_UPDATE_AT, now } =
        await retrieveOTAMeta()

      if (isMoreThan1MinuteSinceLastOTAUpdateApplied) {
        await storageSetWithNoopOnFail(APPLIED_OTA_UPDATE_AT, now.toISO())
        await Updates.reloadAsync()
      }
    }

    if (isUpdatePending) {
      void applyUpdate()
    }
  }, [isUpdatePending])

  if (isDownloading || isUpdatePending) {
    return (
      <SafeAreaView
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'white',
          marginHorizontal: 12,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 32 }}>
          <Logo />
        </View>
        <Text
          style={[
            typography['paragraph/md'],
            {
              marginVertical: 16,
              marginBottom: 40,
              lineHeight: 24,
              color: mesaColorNames['Greyscale/600 #505D64'],
              textAlign: 'center',
            },
          ]}
        >
          Downloading the latest feature updates, security enhancments, and bug fixes.
        </Text>
        <Spinner color={mesaColorNames['Greyscale/500 #6E7375']} scale={1.5} />
      </SafeAreaView>
    )
  } else if (downloadError || checkError) {
    // downloadError && logError(downloadError)
    // checkError && logError(checkError)
    // initializationError && logError(initializationError)
    return children
  } else {
    return children
  }
}

function DelayOTA({ children }: PropsWithChildren) {
  const [initializeOTA, setInitializeOTA] = useState(false)
  const isNewAppInstall = useIsFirstRun()

  useEffect(() => {
    // If the app is a new install, we want to delay downloading any available updates to allow AppsFlyer and other services to do stuff.
    if (isNewAppInstall) {
      const updateDelay = setTimeout(() => setInitializeOTA(true), FIRST_RUN_UPDATE_DELAY)
      return () => clearTimeout(updateDelay)
    } else {
      setInitializeOTA(true)
    }
  }, [isNewAppInstall, initializeOTA])

  return initializeOTA ? <MobileOTA>{children}</MobileOTA> : children
}

function OTA({ children }: PropsWithChildren) {
  switch (Platform.OS) {
    case 'ios':
    case 'android':
      return <DelayOTA>{children}</DelayOTA>
    default:
      return children
  }
}

export const OTAScreen = { name: 'OTA', Screen: OTA } as const
